import { FC, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import ConfirmEmail from 'components/common/ConfirmEmail'
import ErrorServer404 from 'components/core/ErrorBoundary/Error404'
import ErrorNetwork408 from 'components/core/ErrorBoundary/ErrorNetwork408'
import ErrorServer500 from 'components/core/ErrorBoundary/ErrorServer500'
import ErrorSpitter from 'components/core/ErrorBoundary/ErrorSpitter'
import { AdminRoute, LandingRoute, UserRoute } from 'components/core/UserHandler'
import { usePermissions } from 'hooks/usePermissions'
import AccountLinkingPage from 'pages/AccountLinking'
import AccountPage from 'pages/AccountPage'
import AcquiringPage from 'pages/Acquiring'
import BatchPaymentPage from 'pages/BatchPayment'
import CompanyApplicationPage from 'pages/CompanyApplication'
import DynamicFormPage from 'pages/DynamicForm'
import ForgotPasswordPage from 'pages/ForgotPassword'
import IndividualApplicationPage from 'pages/IndividualApplication'
import AccountMockPage from 'pages/MockAccount'
import ResetPasswordPage from 'pages/ResetPassword'
import Handle3DSecurePage from 'pages/Secure3D'
import ServicePlansPage from 'pages/ServicePlans'
import SettingsPage from 'pages/Settings'
import SignInPage from 'pages/SignIn'
import SuccessPage from 'pages/Success'
import TransactionsPage from 'pages/Transactions'
import { finishErrorAlert } from 'redux/alert/alert-actions'
import { useAppSelector } from 'redux/hooks'
import { fetchOnPageLoad as fetchOnPageLoadDispatch } from 'redux/settings/settings-actions'
import { checkUserSession as checkUserSessionDispatch } from 'redux/user/user-actions'
import { clearAccessTokens, getAccessToken, getRefreshToken } from 'utils/accessHelpers'
import { isBetaEnabled } from 'utils/helpers'

import { COMPONENT_EXTENDER } from 'extender'

const RefreshPage = COMPONENT_EXTENDER('components/core/ErrorBoundary/ErrorPage')
const LegalPage = COMPONENT_EXTENDER('pages/Legal')
const TermOfUse = COMPONENT_EXTENDER('components/core/LegalPages/Pages/TermOfUse-component')
const PrivacyPolicy = COMPONENT_EXTENDER('components/core/LegalPages/Pages/PrivacyPolicy-component')
const ComprehensivePrivacyPolicy = COMPONENT_EXTENDER('components/core/LegalPages/Pages/ComprehensivePrivacyPolicy-component')
const RiskWarning = COMPONENT_EXTENDER('components/core/LegalPages/Pages/RiskWarning-component')
const RegulatorySupervision = COMPONENT_EXTENDER('components/core/LegalPages/Pages/RegulatorySupervision-component')
const CookieStatement = COMPONENT_EXTENDER('components/core/LegalPages/Pages/CookieStatement-component')
const Impressum = COMPONENT_EXTENDER('components/core/LegalPages/Pages/Impressum-component')
const AMLCTFPolicy = COMPONENT_EXTENDER('components/core/LegalPages/Pages/AMLCTFPolicy-component')
const Trademarks = COMPONENT_EXTENDER('components/core/LegalPages/Pages/Trademarks-component')
const ContactUs = COMPONENT_EXTENDER('components/core/LegalPages/Pages/ContactUs-component')
const AboutUs = COMPONENT_EXTENDER('components/core/LegalPages/Pages/AboutUs-component')
const Verificationpage = COMPONENT_EXTENDER('pages/Verification')
const Referralspage = COMPONENT_EXTENDER('pages/Referrals')
const SignUpPage = COMPONENT_EXTENDER('pages/SignUp/SignUp')

const MainRoutes: FC = () => {
    const dispatch = useDispatch()
    const data = useLocation()
    const { getPermission } = usePermissions()

    const fetchOnPageLoad = useRef(() => {
        dispatch(fetchOnPageLoadDispatch())
    })

    const checkUserSession = useRef(() => {
        dispatch(checkUserSessionDispatch())
    })

    const { currentUser } = useAppSelector(state => state.user)
    const { error: acquiringError } = useAppSelector(state => state.acquiring)

    useEffect(() => {
        if (acquiringError) {
            dispatch(finishErrorAlert())
        }
    })

    useEffect(() => {
        getRefreshToken() && checkUserSession.current()
        if (!getRefreshToken() || !getAccessToken()) {
            clearAccessTokens()
        }
    }, [checkUserSession])

    useEffect(() => {
        if (!currentUser) {
            fetchOnPageLoad.current()
        }
    }, [currentUser, fetchOnPageLoad])

    return (
        <Routes>
            <Route element={<UserRoute />}>
                <Route path="/" element={<AccountPage />} />
                <Route path="/welcome" element={<AccountPage />} />
                <Route path="/phone-verification" element={<AccountPage />} />
                <Route path="/two-fa-setup" element={<AccountPage />} />
                <Route path="/identity" element={<AccountPage />} />
                <Route path="/questionnaire" element={<AccountPage />} />
                <Route path="/documents" element={<AccountPage />} />
                <Route path="/payment" element={<AccountPage />} />
                <Route path="/thank-you" element={<AccountPage />} />
                <Route path="/verification" element={<Verificationpage />} />
                <Route path="/verification/thank-you" element={<Verificationpage />} />
                <Route path="/card-deposit" element={<Handle3DSecurePage />} />
                <Route path="/bank-deposit" element={<Handle3DSecurePage />} />
                <Route path="/company-card-payment" element={<Handle3DSecurePage />} />
                <Route path="/company-online-bank-payment" element={<Handle3DSecurePage />} />
                <Route path="/company-application-form" element={<CompanyFormAccess />} />
                <Route path="/individual-application-form" element={<IndividualFormAccess />} />
                <Route path="/fill-form" element={<DynamicFormAccess />} />
            </Route>
            <Route element={<UserRoute permission={getPermission('PreviewModeEnabled')} />}>
                <Route path="/preview-mode" element={<AccountMockPage />} />
            </Route>
            <Route
                element={
                    <UserRoute
                        routes={[
                            { path: '/settings/profile' },
                            { path: '/settings/documents', permission: getPermission('SettingsTabsDocuments') },
                            { path: '/settings/users', permission: getPermission('SettingsTabsUsers') },
                            { path: '/settings/fees', permission: getPermission('SettingsTabsServices') },
                            { path: '/settings/affiliate', permission: getPermission('SettingsTabsAffiliate') },
                            { path: '/settings/support' },
                            { path: '/settings/notifications', permission: getPermission('SettingsTabsNotifications') },
                            { path: '/settings/security' },
                        ]}
                    />
                }
            >
                <Route path="/settings/:componentName" element={<SettingsPage />} />
            </Route>
            <Route element={<UserRoute permission={getPermission('SettingsTabsServices')} />}>
                <Route path="/service-plans" element={<ServicePlansPage />} />
            </Route>
            <Route
                element={
                    <UserRoute
                        routes={[
                            { path: '/transactions/history' },
                            { path: '/transactions/statement' },
                            { path: '/transactions/import-of-payments' },
                        ]}
                    />
                }
            >
                <Route path="/transactions/:componentName" element={<TransactionsPage />} />
            </Route>
            <Route element={<UserRoute />}>
                <Route path="/import-of-payments/:batchId" element={<BatchPaymentPage />} />
            </Route>
            <Route
                element={
                    <UserRoute
                        permission={getPermission('CardAcquiring')}
                        routes={[
                            { path: '/payment-gateway/overview' },
                            { path: '/payment-gateway/charges' },
                            { path: '/payment-gateway/settlements' },
                            { path: '/payment-gateway/api' },
                        ]}
                    />
                }
            >
                <Route path="/payment-gateway/:componentName" element={<AcquiringPage />} />
            </Route>
            <Route element={<UserRoute permission={getPermission('ReferralProgram')} />}>
                <Route path="/referrals" element={<Referralspage />} />
            </Route>
            <Route element={<LandingRoute permission={getPermission('LegalPage')} />}>
                <Route element={<LegalPage />}>
                    <Route path="/legal" element={<TermOfUse />} />
                    <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/legal/impressum" element={<Impressum />} />
                    <Route path="/legal/cookie-statement" element={<CookieStatement />} />
                    <Route path="/legal/trademarks" element={<Trademarks />} />
                    <Route path="/legal/contact-us" element={<ContactUs />} />
                    <Route path="/legal/about-us" element={<AboutUs />} />
                </Route>
            </Route>
            <Route element={<LandingRoute />}>
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/sign-up" element={<SignUpPage />} />
            </Route>
            <Route path="/vQXYNn0q7webmx7aOZ9S4sbPOmAQwJoA/:accessToken" element={<AdminRoute />} />
            <Route path="/success" element={<EmailVerificationRouting />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/link/:accountlinkcode" element={<AccountLinkingPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:passwordResetRequestId" element={<PasswordResetRouting />} />
            <Route path="/server-error-500" element={<ErrorServer500 />} />
            <Route path="/server-error-404" element={<ErrorServer404 />} />
            <Route path="/network-error" element={<ErrorNetwork408 />} />
            <Route path="/not-found" element={<ErrorServer404 />} />
            <Route path="/refresh-page" element={<RefreshPage />} />
            {isBetaEnabled && <Route path="/error-spitter" element={<ErrorSpitter />} />}
            <Route path="*" element={<UserRoute notFound />} />
        </Routes>
    )

    function IndividualFormAccess() {
        if (data?.state?.documentNo && data?.state?.approved) {
            return <IndividualApplicationPage />
        } else {
            return <Navigate replace to="/" />
        }
    }

    function DynamicFormAccess() {
        if (data?.state?.documentNo && data?.state?.approved) {
            return <DynamicFormPage />
        } else {
            return <Navigate replace to="/" />
        }
    }

    function CompanyFormAccess() {
        if (data?.state?.documentNo && data?.state?.approved) {
            return <CompanyApplicationPage />
        } else {
            return <Navigate replace to="/" />
        }
    }

    function EmailVerificationRouting() {
        if (!currentUser && !getRefreshToken()) {
            return <Navigate replace to="/sign-in" />
        } else if (!!currentUser && !currentUser?.isEmailConfirmed) {
            return <SuccessPage />
        } else {
            return <Navigate replace to="/" />
        }
    }

    function PasswordResetRouting() {
        clearAccessTokens()

        return <ResetPasswordPage />
    }
}

export default MainRoutes
